import React, { useState, useRef, useEffect, useMemo } from "react";
import { Card, CardBody, Table, Row, Col, Input, Button, CardHeader } from "reactstrap";
import { getDateByName, convertDateFormate } from "../../helpers/api_helper";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import Loader from "../../Components/Common/Loader";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";
import ReactECharts from "echarts-for-react";


// Your color palette
const coverColor = [
  "#405189", "#0ab39c", "#f7b84b", "#f06548", "#33DAFF", "#299cdb",
  "#FFA233", "#C1FF33", "#BE33FF", "#FF33A5", "#33FF8D", "#3577f1",
  "#FF80C9", "#FFB3DE", "#FF8085", "#FFB3B6", "#FF80E6", "#FFB3F0",
  "#DE80FF", "#EBB3FF", "#AC80FF", "#CDB3FF", "#B3D7FF", "#80DEFF",
  "#B3EBFF", "#80FFB0", "#AEFF80", "#EEFF66", "#FFA34D", "#FFCB99"
];

const Benchmark = ({activeDate,  customDate, currency, section="", mealPeriod=""}) => {
    const [categories, setCategories] = useState([]);
    const [staffData, setStaffData] = useState([]);
    const [selectedMetric, setSelectedMetric] = useState("avg_check");
    const [activeStaff, setActiveStaff] = useState([]);
    const [courseArr, setCourseArr] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [staffList, setStaffList] = useState([]);
    const [isInitialLoaded, setIsInitialLoaded] = useState(true);

    const [popup, setPopup] = useState(null);
    const cellRefs = useRef({});
    const tableWrapperRef = useRef(null);
    const chartWrapperRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [trendSeries, setTrendSeries] = useState([]);
    const [trendWeeks, setTrendWeeks] = useState([]);
    const [loading1, setLoading1] = useState(false);
    const [staffColorMap, setStaffColorMap] = useState({});
    const [maxCover, setMaxCover] = useState(0);
       
    var datefrom =convertDateFormate(customDate[0]);
    var dateto = convertDateFormate(customDate[1]);
    let dateval =
            activeDate.value === "Custom"
            ? { from: datefrom, to: dateto }
            : getDateByName(activeDate.value);

    const { courses } = useSelector((state) => ({
      courses: state.Dashboard.filterData.courses || [],
    }));
    
    const courseColorMap = useMemo(() => {
          const map = new Map();
    
          courses.forEach(course => {
              if (course.label) {
                  map.set(course.label, {
                      bg: course.badge_bg || null,
                      text: course.badge_text || null,
                  });
              }
          });
    
          return map;
      }, [courses]);
    
      /**
       * Get full badge style by label
       */
      const getColorByLabel = (label) => {
          const colorObj = courseColorMap.get(label);
    
          if (!colorObj || !colorObj.bg) {
              return {
                backgroundColor: "#405189",
                color: "#FFFFFF"
              }; // No color assigned
          }
    
          return {
              backgroundColor: colorObj.bg,
              color: colorObj.text || "#000000",
          };
      };

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        postDataAPI({
            dateFilter: dateval,
            mealPeriod,
            serviceName: "kpi_data",
            op: "kpi_server_course_performance"
        }, controller.signal).then((response) => {
            setLoading(false);
            if (response.status !== "ok" || !response.data) {
                resetAllStates();
                return;
            } else if (response.status === "ok") {
                const api = response.data;

                // If API returned empty array or empty object → reset
                if (!api || Array.isArray(api) || Object.keys(api).length === 0) {
                    resetAllStates();
                    return;
                }
                // STEP 1: categories from courseArr
                const categories = Object.keys(api.courseArr); 
                setStaffList(api.serverOptions); 
                if (api.serverOptions.length > 0) {
                  setActiveStaff([api.serverOptions[0].server_id]); 
                }

                const colorMap = {};
                api.serverOptions.forEach((s, i) => {
                    colorMap[s.server_id] = coverColor[i % coverColor.length];
                });
                setStaffColorMap(colorMap);
                
                //console.log(api.courseArr);
                           
                const convertedCat = Object.keys(api.courseArr).map(key => ({
                    name: key,
                    value: api.courseArr[key]
                }));
                //console.log(convertedCat);
                
                setCourseArr(convertedCat);
                // STEP 2: staff rows
                const staff = api.serverOptions.map(s => s.name);

                // STEP 3: map category name → its series data
                const seriesMap = {};
                api.serverSeries.forEach(series => {
                    seriesMap[series.name] = series;
                });

                // STEP 4: Build staffData dynamically
                const formatted = staff.map((staffName, index) => {
                    const performance = categories.map((cat) => {
                        const perf = seriesMap[cat]?.performer_data[index];
                        if (!perf) return null;

                        if (perf.toLowerCase().includes("top")) return "top";
                        if (perf.toLowerCase().includes("bottom")) return "bottom";
                        if (perf.toLowerCase().includes("contribution")) return "na";
                        return "improve"; 
                    });

                    const details = categories.reduce(
                        (obj, cat) => {
                            const s = seriesMap[cat];

                            obj[cat] = {
                                sales: `${currency}${s?.data[index] ?? 0}`,
                                check: `${currency}${s?.rev_avg_data[index] ?? 0}`,
                                rate: `${s?.contribution_rate[index] ?? 0}%`
                            };

                            return obj;
                        },
                        {}
                    );

                    return {
                        name: staffName,
                        performance,
                        details
                    };
                });

                setCategories(categories);
                setStaffData(formatted);
                setSelectedCourse(Object.values(api.courseArr)[0]);
                setIsInitialLoaded(true);
            }
        });
        return () => {
            controller.abort();
        };
    }, [activeDate, mealPeriod, customDate]);


  const metrics = [
    { name: `Average Check (${currency})`, value: "avg_check" },
    { name: "Item Sold", value: "items_sold" }
  ];

  function resetAllStates() {
    setCategories([]);
    setStaffData([]);
    setCourseArr([]);
    setSelectedCourse(null);
    setStaffList([]);
    setActiveStaff([]);
    setTrendSeries([]);
    setTrendWeeks([]);
  }

  const handleCellClick = (row, col) => {
    const key = `${row}-${col}`;
    const cell = cellRefs.current[key];
    const wrap = tableWrapperRef.current;
    
    if (!cell || !wrap) return;

    const cellRect = cell.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    setPopup({
      row,
      col,
      top: (cellRect.bottom - wrapRect.top + 10) - 30,
      left: cellRect.left - wrapRect.left + cellRect.width / 2,
      staff: staffData[row].name,
      details: staffData[row].details[categories[col]],
      badge:
        staffData[row].performance[col] === "top"
          ? "Top Performer"
          : staffData[row].performance[col] === "improve"
          ? "Improvement Needed"
          : staffData[row].performance[col] === "na"
          ? "No Contribution"
          : "Bottom Performer",
    });
  };

  // Close popup when clicking outside
  useEffect(() => {
    const close = (e) => {
      if (tableWrapperRef.current && !tableWrapperRef.current.contains(e.target)) {
        setPopup(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

    const getBadgeClass = (badge) => {
        if (badge.includes("Top")) return "badge-top";
        if (badge.includes("Improve")) return "badge-improve";
        if (badge.includes("Contribution")) return "badge-na";
        return "badge-bottom";
    };

    const getCategoryColor = (index) => {
        return coverColor[index % coverColor.length];
    };

    const toggleStaff = (serverId) => {
      setActiveStaff((prev) =>
        prev.includes(serverId)
          ? prev.filter((id) => id !== serverId)  // remove
          : [...prev, serverId]                   // add
      );
    };

    const getEchartOptions = () => {
        return {
          tooltip: {
            trigger: "axis",
            formatter: function (params) {
              let result = `${params[0].axisValue}<br/>`;

              params.forEach((item) => {
                if (item.seriesName.includes("Avg Check")) {
                  result += `${item.marker} ${item.seriesName}: <b>${currency}${item.value}</b><br/>`;
                } else {
                  result += `${item.marker} ${item.seriesName}: <b>${item.value}</b><br/>`;
                }
              });

              return result;
            },
          },

          legend: {
            bottom: 0,        // keep at bottom
            left: "center",
          },

          grid: {
            left: "5%",
            right: "5%",
            bottom: 80,       // ⭐ increase this (key fix)
            containLabel: true,
          },
          xAxis: {
            type: "category",
            data: trendWeeks,
          },

          yAxis: [
            {
              type: "value",
              name: "Avg Check",
              position: "left",
              axisLabel: {
                formatter: (val) => val.toFixed(0),
              },
              min: 0,
              max: (value) => {
                return Math.ceil(value.max / 5) * 5; // normalize
              },
              splitLine: {
                show: false,
              },
            },
            {
              type: "value",
              name: "Item Sold",
              position: "right",
              axisLabel: {
                formatter: (val) => val.toFixed(0),
              },
              min: 0,
              max: (value) => {
                return Math.ceil(value.max / 5) * 5; // same logic
              },
              splitLine: {
                show: false,
              },
            },
          ],

          series: trendSeries.map((s) => {
            const isLine = s.type === "line";

            return {
              name: s.name,
              type: isLine ? "line" : "bar",
              data: s.data,
              yAxisIndex: isLine ? 0 : 1, // ✅ PERFECT AXIS BINDING
              smooth: isLine,
              symbolSize: isLine ? 8 : 0,
              lineStyle: isLine
                ? { width: 3 }
                : undefined,
              itemStyle: {
                color: s.color,
              },
              barMaxWidth: isLine ? undefined : 30,
            };
          }),
        };
      };


    useEffect(() => {
      if (!isInitialLoaded) return;
      if (!selectedCourse || activeStaff.length === 0) return;

      getTrendsData();

    }, [selectedCourse, selectedMetric, activeStaff, mealPeriod, customDate]);

async function getTrendsData () {
  setLoading1(true);
  const controller = new AbortController();  
  postDataAPI({
    dateFilter: dateval,
    serviceName: "kpi_data",
    op: "kpi_server_course_performance_trends",
    server_id: activeStaff,
    course: selectedCourse,
    metric: "",
    mealPeriod
  }, controller.signal).then((response) => {
    setLoading1(false);

    if (
      response.status !== "ok" ||
      !response.data ||
      !Array.isArray(response.data.weeksData) ||
      response.data.weeksData.length === 0
    ) {
      setTrendSeries([]);
      setTrendWeeks([]);
      return;
    }

    const api = response.data;

    const weekKeys = [
      "Current Week",
      "Previous Week",
      "Curr. -3w",
      "Curr. -4w",
      "Curr. -5w",
      "Curr. -6w",
      "Curr. -7w",
      "Curr. -8w"
    ];

    setTrendWeeks(weekKeys);

    const finalSeries = [];
    let maxCoverValue = 0;

    api.weeksData.forEach((item) => {
      const staffId = staffList.find(s => s.name === item.name)?.server_id;
      const color = staffColorMap[staffId];

      const avgData = weekKeys.map(k => Number(item.avg_check?.[k] ?? 0));
      const itemSoldData = weekKeys.map(k => Number(item.items_sold?.[k] ?? 0));

      finalSeries.push({
        name: `${item.name} Avg Check`,
        type: "line",
        data: avgData,
        color,
      });

      finalSeries.push({
        name: `${item.name} Item Sold`,
        type: "bar",
        data: itemSoldData,
        color,
      });

      maxCoverValue = Math.max(maxCoverValue, ...itemSoldData);
    });

    setTrendSeries(finalSeries);
    setMaxCover(Math.ceil(maxCoverValue * 1.1));

  });
  return () => {
      controller.abort();
  };
}




  return (
    <>
    {(section === "" || section === "team-benchmrk") && (
    <Card>
        <CardHeader className="ps-3 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-start">
              <div className={`icon-box rounded bg-danger text-white`}>
                  <i className="ri-line-chart-fill"></i>
              </div>
              <div className="ps-2">
                <h4 className="card-title mb-1">Team Benchmark</h4>
                <p className="text-muted mb-0">
                  Staff performance across courses - identify top performers and improvement opportunities
                </p>
              </div>
            </div>
          </CardHeader>
        <CardBody>
        {loading && <Loader />}
        <div ref={tableWrapperRef}
        className={
              loading
                ? "opacity-0 table-responsive table-card"
                : "table-responsive table-card"}
        >
          {/* TABLE */}
          <Table borderless className="benchmark-table">
            <thead>
              <tr>
                <th className="staff-header">Staff Member</th>
                {/* {categories.map((cat, index) => (
                  <th key={cat} className="text-center">
                    <span className={`cat-badge`} style={{ getColorByLabel(cat), fontSize: "10px", padding: "5px 10px" }}>
                      {cat}
                    </span>
                  </th>
                ))} */}
                {categories.map((cat) => { 
                  const badgeColor = getColorByLabel(cat);
                  return (
                  <th key={cat} className="text-center">
                    <span
                      className="cat-badge"
                      style={{
                        ...badgeColor,
                        fontSize: "10px",
                        padding: "5px 10px",
                        borderRadius: "12px"    // optional, badge look
                      }}
                    >
                      {cat}
                    </span>
                  </th>
                )})}
              </tr>
            </thead>

            <tbody>
              {staffData.map((person, row) => (
                <tr key={row}>
                  <td className="staff-name">{person.name}</td>

                  {person.performance.map((perf, col) => {
                    const key = `${row}-${col}`;
                    return (
                      <td
                        key={col}
                        className="text-center dot-cell"
                        ref={(el) => (cellRefs.current[key] = el)}                        
                      >
                        {perf && (
                          <span
                            className={`dot dot-${perf}`}
                            title={perf}
                            onClick={() => perf && handleCellClick(row, col)}
                          ></span>
                          
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>

          {/* POPUP */}
          {popup && (
           <div
            className="popup-card"
            style={{
                top: popup.top,
                left: popup.left,
                transform: "translateX(-50%)",
            }}
            >
            <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{popup.staff}</strong>
                <span className={`popup-badge ${getBadgeClass(popup.badge)}`}>{popup.badge}</span>
            </div>

            <hr className="my-2" />

            {/* 2-column layout */}
            <div className="popup-row">
                <span className="popup-label">Total Sales:</span>
                <span className="popup-value">{popup.details.sales}</span>
            </div>

            <div className="popup-row">
                <span className="popup-label">Avg Check:</span>
                <span className="popup-value">{popup.details.check}</span>
            </div>

            <div className="popup-row">
                <span className="popup-label">Conversion Rate:</span>
                <span className="popup-value">{popup.details.rate}</span>
            </div>

            </div>
          )}

          {/* LEGEND */}
          <div className="legend mt-4 p-3">
            <span>
              <span className="dot dot-top"></span>Top performer
            </span>
            <span>
              <span className="dot dot-improve"></span>Improvement needed
            </span>
            <span>
              <span className="dot dot-bottom"></span>Bottom performer
            </span>
            <span>
              <span className="dot dot-na"></span>N/A
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
    )}
    {(section === "" || section === "team-trends") && (
      <Card>
          <CardHeader className="ps-3 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-start">
              <div className="icon-box rounded bg-danger text-white">
                <i className="ri-bar-chart-line"></i>
              </div>
              <div className="ps-2">
                <h4 className="card-title mb-1">Performance Trends (Last 8 Weeks - Same Day of week)</h4>
                <p className="text-muted mb-0">
                  Track performance trends over time for selected staff members
                </p>
              </div>
            </div>
          </CardHeader>
    
          <CardBody>
           
            <Row className="g-4 mb-3">
              <Col md={4}>
                <label className="fw-semibold mb-1">&nbsp;Course Category</label>
                <Input
                  type="select"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                >
                  {courseArr.map((item) => (
                    <option key={item.name} value={item.value}>
                      {item.name}
                    </option>
                  ))}
                </Input>
              </Col>
    
              {/* <Col md={4}>
                <label className="fw-semibold mb-1">&nbsp;Metric</label>
                <Input type="select" value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                >
                  {metrics.map((item) => (
                    <option key={item.name} value={item.value}>
                      {item.name}
                    </option>
                  ))}
                </Input>
              </Col> */}
            </Row>
    
            {/* Staff selection badges */}
            <div className="mb-3">
              <label className="fw-semibold mb-2">Select Staff:</label>

              <div className="d-flex flex-wrap gap-2">
                {staffList.map((staff, index) => {
                  const isActive = activeStaff.includes(staff.server_id);

                  return (
                    <Button
                      key={staff.server_id}
                      className="px-3"
                      style={{
                        background: isActive ? staffColorMap[staff.server_id] : "#f5f5f5",
                        color: isActive ? "white" : "#444",
                        border: isActive ? "none" : "1px solid #ccc"
                      }}
                      onClick={() => toggleStaff(staff.server_id)}
                    >
                      {staff.name}
                    </Button>
                  );
                })}
              </div>
            </div>

          {loading1 && <Loader />}
            <div ref={chartWrapperRef}
            className={
                  loading1
                    ? "opacity-0"
                    : ""}
            >
            {/* Chart */}
            {/* <Chart options={options} series={trendSeries} type="line" height={350} /> */}
            <ReactECharts
              option={getEchartOptions()}
              style={{ height: 370 }}
              notMerge={true}
            />
          </div>
          </CardBody>
      </Card>
    )}
    </>
  );
};

export default Benchmark;
